import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import { PaymentRoundSummary } from 'src/app/admin-api';
import { PaymentRoundControllerService } from 'src/app/admin-api/api/paymentRoundController.service';
import { TableActionButton, TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import { PaymentRoundReportModalComponent } from '../payment-round-report-modal/payment-round-report-modal.component';

@Component({
  selector: 'app-scheduler-jobs-payment-rounds',
  templateUrl: './scheduler-jobs-payment-rounds.component.html',
  styleUrls: ['./scheduler-jobs-payment-rounds.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe]
})
export class SchedulerJobsPaymentRoundsComponent implements OnInit {
  private paymentRoundService = inject(PaymentRoundControllerService);
  private dialogService = inject(DialogService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private datePipe = inject(DatePipe);

  private static readonly REFERENCE_MONTH_PARAM = 'roundsReferenceMonth';

  selectedMonthLabel = '';

  monthFilterForm = new FormGroup({
    month: new FormControl<Date>(new Date(), Validators.required)
  });

  paymentRounds: Array<PaymentRoundSummary & { conversionRate: number, status: 'Concluído' | 'Em execução' | 'Agendada' | 'Não executada' }> = [];
  paymentRoundCols: TableColumn[] = [
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('status')
      .setType('status')
      .setStatusFunction((item: PaymentRoundSummary) => {
        switch (this.getStatusLabel(item)) {
          case 'Concluído':
            return 1;
          case 'Não executada':
            return -1;
          default:
            return 0;
        }
      })
      .build(),
    new TableColumn.Builder()
      .setHeader('Agendado para')
      .setField('dateScheduled')
      .setType('date')
      .setCondition('between')
      .setDisplayFunction((item: PaymentRoundSummary) => item.dateScheduled ? this.datePipe.transform(item.dateScheduled, 'dd/MM/yyyy HH:mm') : 'Execução manual')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data execução')
      .setField('dateExecuted')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('Agendado por')
      .setField('username')
      .setCondition('startsWith')
      .build(),
    new TableColumn.Builder()
      .setHeader('Round ID')
      .setField('paymentRoundId')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Assinantes')
      .setField('totalSubscribers')
      .setType('formattedInteger')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Benefícios')
      .setField('totalSubscribersBenefit')
      .setType('formattedInteger')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Sucesso')
      .setField('paymentSuccess')
      .setType('formattedInteger')
      .setCondition('gte')
      .build(),
    new TableColumn.Builder()
      .setHeader('Taxa conversão')
      .setField('conversionRate')
      .setType('percentage')
      .setCondition('gte')
      .build(),
    new TableColumn.Builder()
      .setHeader('Benefícios falha')
      .setField('failurePaymentBenefit')
      .setType('formattedInteger')
      .setCondition('equals')
      .setDisplayFunction((item: PaymentRoundSummary & { conversionRate: number }) => (item.failureActivationBenefit || 0) + (item.failureEditionBenefit || 0) + (item.failurePaymentBenefit || 0) + (item.failureValidationBenefit || 0))
      .build(),
    new TableColumn.Builder()
      .setHeader('Acoes')
      .setType('button')
      .setFilter(false)
      .setSortable(false)
      .build()
  ];

  paymentRoundActions: TableActionButton[] = [
    new TableActionButton(
      '',
      (item: PaymentRoundSummary) => this.openReportModal(item),
      'pi pi-search',
      (item: PaymentRoundSummary) => this.canViewReport(item),
      '',
      'Visualizar relatório',
      'bottom',
      true,
      true,
      'secondary',
      'small'
    )
  ];

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    try {
      this.syncMonthFromQueryParam();
      await this.syncReferenceMonthQueryParam();
      await this.loadPaymentRoundsBySelectedMonth();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async goToPreviousMonth(): Promise<void> {
    const currentMonth = this.monthFilterForm.controls.month.value;
    if (!currentMonth) {
      return;
    }

    const previousMonth = new Date(currentMonth);
    previousMonth.setMonth(previousMonth.getMonth() - 1);
    this.monthFilterForm.controls.month.setValue(previousMonth);
    await this.applyMonthFilter();
  }

  async goToNextMonth(): Promise<void> {
    const currentMonth = this.monthFilterForm.controls.month.value;
    if (!currentMonth) {
      return;
    }

    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    this.monthFilterForm.controls.month.setValue(nextMonth);
    await this.applyMonthFilter();
  }

  async applyMonthFilter(): Promise<void> {
    LoaderService.showLoader();
    try {
      await this.syncReferenceMonthQueryParam();
      await this.loadPaymentRoundsBySelectedMonth();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  openReportModal(item: PaymentRoundSummary): void {
    const effectiveDate = item?.dateScheduled ?? item?.dateExecuted;
    const dateStr = effectiveDate
      ? new Date(effectiveDate as string | Date).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
      : '';

    this.dialogService.open(PaymentRoundReportModalComponent, {
      data: {
        job: item,
        loadOnInit: false
      },
      header: `Relatorio de Cobranca${dateStr ? ' - ' + dateStr : ''}`,
      width: '680px',
      contentStyle: { overflow: 'auto' }
    });
  }

  private async loadPaymentRoundsBySelectedMonth(): Promise<void> {
    const selectedMonth = this.monthFilterForm.controls.month.value;
    if (!selectedMonth) {
      return;
    }

    const startDate = FormUtil.utcDate(new Date(
      selectedMonth.getFullYear(),
      selectedMonth.getMonth(),
      1,
      0,
      0,
      0,
      0
    ));
    const endDate = FormUtil.utcDate(new Date(
      selectedMonth.getFullYear(),
      selectedMonth.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    ));

    const rounds = await lastValueFrom(
      this.paymentRoundService
        .getPaymentRoundsSummaryByPeriod(startDate, endDate)
        .pipe(map((data) => data.result || []))
    );

    this.selectedMonthLabel = new Intl.DateTimeFormat('pt-BR', {
      month: 'long',
      year: 'numeric'
    }).format(startDate);

    this.paymentRounds = (rounds || []).sort(
      (a, b) =>
        this.getScheduledDate(b).getTime() - this.getScheduledDate(a).getTime()
    ).map(r => ({ ...r, conversionRate: this.conversionRate(r), status: this.getStatusLabel(r) }));
  }

  private conversionRate(item: PaymentRoundSummary): number {
    const total = (item.totalProcessed || 0) - (item.totalProcessedBenefit || 0);
    const success = (item.paymentSuccess || 0) - (item.paymentSuccessBenefit || 0);
    return total > 0 ? (success / total) : 0;
  }

  private canViewReport(item: PaymentRoundSummary): boolean {
    if (!item?.paymentRoundId) {
      return false;
    }

    return (
      !!item.dateExecuted
    );
  }

  private getStatusLabel(item: PaymentRoundSummary): 'Concluído' | 'Em execução' | 'Agendada' | 'Não executada' {

    if (item?.dateExecuted) {
      if (item.totalProcessed >= item.totalSubscribers)
        return 'Concluído';
      return 'Em execução';
    }

    if (this.isPastRound(item))
      return 'Não executada';

    return 'Agendada';
  }

  private isPastRound(item: PaymentRoundSummary): boolean {
    const scheduledDate = this.getScheduledDate(item);
    if (Number.isNaN(scheduledDate.getTime())) {
      return false;
    }

    return scheduledDate < this.startOfToday();
  }

  private isTodayRound(item: PaymentRoundSummary): boolean {
    const scheduledDate = this.getScheduledDate(item);
    if (Number.isNaN(scheduledDate.getTime())) {
      return false;
    }

    return (
      scheduledDate >= this.startOfToday() &&
      scheduledDate < this.startOfTomorrow()
    );
  }

  private getScheduledDate(item: PaymentRoundSummary): Date {
    return new Date((item.dateScheduled ?? item.dateExecuted) as string | Date);
  }

  private startOfToday(): Date {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private startOfTomorrow(): Date {
    const date = this.startOfToday();
    date.setDate(date.getDate() + 1);
    return date;
  }

  private syncMonthFromQueryParam(): void {
    const referenceMonth = this.activatedRoute.snapshot.queryParamMap.get(
      SchedulerJobsPaymentRoundsComponent.REFERENCE_MONTH_PARAM
    );
    const parsedMonth = this.parseReferenceMonth(referenceMonth);
    if (!parsedMonth) {
      return;
    }

    this.monthFilterForm.controls.month.setValue(parsedMonth);
  }

  private async syncReferenceMonthQueryParam(): Promise<void> {
    const selectedMonth = this.monthFilterForm.controls.month.value;
    if (!selectedMonth) {
      return;
    }

    const referenceMonth = this.formatReferenceMonth(selectedMonth);
    const currentReferenceMonth =
      this.activatedRoute.snapshot.queryParamMap.get(
        SchedulerJobsPaymentRoundsComponent.REFERENCE_MONTH_PARAM
      );

    if (currentReferenceMonth === referenceMonth) {
      return;
    }

    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        [SchedulerJobsPaymentRoundsComponent.REFERENCE_MONTH_PARAM]:
          referenceMonth
      },
      queryParamsHandling: 'merge'
    });
  }

  private parseReferenceMonth(value: string | null): Date | null {
    if (!value) {
      return null;
    }

    const normalized = value.trim();
    const isoMatch = /^(\d{4})-(\d{2})$/.exec(normalized);
    if (isoMatch) {
      const year = Number(isoMatch[1]);
      const month = Number(isoMatch[2]) - 1;
      if (month >= 0 && month <= 11) {
        return new Date(year, month, 1, 0, 0, 0, 0);
      }
      return null;
    }

    const brMatch = /^(\d{2})\/(\d{4})$/.exec(normalized);
    if (brMatch) {
      const month = Number(brMatch[1]) - 1;
      const year = Number(brMatch[2]);
      if (month >= 0 && month <= 11) {
        return new Date(year, month, 1, 0, 0, 0, 0);
      }
      return null;
    }

    return null;
  }

  private formatReferenceMonth(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }
}
